import React, { useState } from 'react';
import { useApi } from '../../Api';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { 
  BalanceSheetProps, 
  BalanceSheetResponse, 
  BalanceSheetRequest 
} from '../types/BalanceSheet';
import { BALANCE_SHEET_TYPE_OPTIONS, ReportType } from '../../constants/accounting';
import { FormSelect } from '../../controls/FormSelect';
import CustomInput from '../../controls/CustomInput';

const BalanceSheet: React.FC<BalanceSheetProps> = ({ familyId, locationId }) => {
  const api = useApi();
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const [request, setRequest] = useState<BalanceSheetRequest>({
    reportEndDate: new Date().toISOString().split('T')[0],
    type: 'unconsolidated',
    journalStatus: 'all'
  });

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionLabel: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionLabel)) {
      newExpanded.delete(sectionLabel);
    } else {
      newExpanded.add(sectionLabel);
    }
    setExpandedSections(newExpanded);
  };

  const isSectionExpanded = (sectionLabel: string): boolean => {
    return expandedSections.has(sectionLabel);
  };

  const fetchBalanceSheet = async () => {
    setLoading(true);
    setError(null);
    setNoData(false);
    
    try {
      // Build params object - only include comparative columns if both are provided
      const params: any = {
        familyId: familyId,
        reportEndDate: request.reportEndDate,
        type: request.type,
        journalStatus: request.journalStatus,
        ...(locationId && { locationId: locationId })
      };

      // Only include comparative columns if both type and count are provided
      if (request.comparativeColumnType && request.comparativeColumnCount) {
        params.comparativeColumnType = request.comparativeColumnType;
        params.comparativeColumnCount = request.comparativeColumnCount;
      }

      const response = await api.get('/api/accounting/BalanceSheet', { params });
      
      // The backend returns a BalanceSheetResponse object with a BalanceSheet property
      if (response.data && response.data.balanceSheet) {
        setBalanceSheet(response.data.balanceSheet);
        setError(null); // Clear any previous errors
        setNoData(false);
        setLoading(false);
      } else {
        // No data is not an error - just set empty state
        setBalanceSheet(null);
        setError(null);
        setNoData(true);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('Error fetching balance sheet:', err);
      setError(err.response?.data?.message || 'An error occurred while fetching the balance sheet');
      setBalanceSheet(null);
    } finally {
      setLoading(false);
    }
  };

  const validateDates = (): string | null => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    
    const reportEndDate = new Date(request.reportEndDate);
    if (reportEndDate > today) {
      return 'Report end date cannot be in the future';
    }
    
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const dateError = validateDates();
    if (dateError) {
      setError(dateError);
      return;
    }
    
    fetchBalanceSheet();
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getValueColor = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'text-gray-800';
    if (num > 0) return 'text-green-600';
    if (num < 0) return 'text-red-600';
    return 'text-gray-800';
  };

  const getSectionTotal = (sectionLabel: string): number => {
    const section = balanceSheet?.rows.find(row => 
      row.rowType === 'summary' && 
      row.label === `Total ${sectionLabel}`
    );
    
    if (section && section.rowType === 'summary') {
      return section.value;
    }
    return 0;
  };

  const renderRow = (row: any, index: number) => {
    if (row.rowType === 'header') {
      // Main section headers (ASSETS, LIABILITIES & EQUITY)
      if (row.depth === 0) {
        return (
          <div key={index} className="font-bold text-xl text-gray-900 py-4 px-4 bg-gray-100 border-b border-gray-300 my-3 rounded">
            {row.label}
          </div>
        );
      }
      
      // Sub-section headers (Current Assets, Fixed Assets, etc.)
      if (row.depth === 1) {
        const sectionTotal = getSectionTotal(row.label);
        
        return (
          <div key={index} className="border-b border-gray-200">
            <div className="flex justify-between items-center py-3 px-4 bg-white">
              <span className="font-semibold text-base text-gray-800">{row.label}</span>
              <span className={`font-semibold text-base ${getValueColor(sectionTotal.toString())}`}>
                {formatCurrency(sectionTotal.toString())}
              </span>
            </div>
          </div>
        );
      }
      
      // Account group headers (Cash, Accounts Receivable, etc.)
      if (row.depth >= 2) {
        const sectionTotal = getSectionTotal(row.label);
        const expanded = isSectionExpanded(row.label);
        
        const hasAccounts = balanceSheet?.rows.some((r, rIndex) => {
          if (rIndex > index && 
              r.rowType === 'accounts' && 
              r.accountBalances && 
              r.accountBalances.length > 0 &&
              r.depth === row.depth + 1) {
            
            const hasIntermediateHeaders = balanceSheet.rows.slice(index + 1, rIndex).some(betweenRow => 
              betweenRow.rowType === 'header'
            );
            
            return !hasIntermediateHeaders;
          }
          return false;
        });
        
        if (hasAccounts) {
          return (
            <div key={index} className="border-b border-gray-100">
              <button
                onClick={() => toggleSection(row.label)}
                className="w-full flex justify-between items-center py-2 px-6 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-baseline space-x-2">
                  <span className={`transform transition-transform duration-200 text-gray-400 text-2xl leading-none ${expanded ? 'rotate-90' : ''}`}>
                    ›
                  </span>
                  <span className="font-medium text-sm text-gray-700">{row.label}</span>
                </div>
                <span className={`font-medium text-sm ${getValueColor(sectionTotal.toString())}`}>
                  {formatCurrency(sectionTotal.toString())}
                </span>
              </button>
            </div>
          );
        } else {
          return (
            <div key={index} className="border-b border-gray-100">
              <div className="flex justify-between items-center py-2 px-6 bg-white">
                <span className="font-medium text-sm text-gray-700">{row.label}</span>
                <span className={`font-medium text-sm ${getValueColor(sectionTotal.toString())}`}>
                  {formatCurrency(sectionTotal.toString())}
                </span>
              </div>
            </div>
          );
        }
      }
      
      return (
        <div key={index} className="font-medium text-sm text-gray-600 py-2 px-8">
          {row.label}
        </div>
      );
    }
    
    if (row.rowType === 'accounts') {
      if (row.accountBalances && row.accountBalances.length > 0) {
        return (
          <div key={index} className="space-y-1">
            {row.accountBalances && row.accountBalances.length > 0 ? (
              (() => {
                let parentSectionLabel = '';
                for (let i = index - 1; i >= 0; i--) {
                  const prevRow = balanceSheet?.rows[i];
                  if (prevRow && prevRow.rowType === 'header' && prevRow.depth >= 1) {
                    parentSectionLabel = prevRow.label;
                    break;
                  }
                }
                
                if (parentSectionLabel && isSectionExpanded(parentSectionLabel)) {
                  return row.accountBalances.map((account: any, accIndex: number) => (
                    <div key={accIndex} className="flex justify-between items-center py-1 px-8 text-sm bg-white">
                      <span className="text-gray-700">
                        {account.accountNumber} - {account.accountName}
                      </span>
                      <span className={`font-mono ${getValueColor(account.balance.toString())}`}>
                        {formatCurrency(account.balance.toString())}
                      </span>
                    </div>
                  ));
                } else {
                  return null;
                }
              })()
            ) : (
              <div className="py-1 px-8 text-sm text-gray-500">
                No account details available
              </div>
            )}
          </div>
        );
      }
      
      return (
        <div key={index} className="py-1 px-8 text-sm text-gray-500">
          No account details available
        </div>
      );
    }
    
    if (row.rowType === 'calculated') {
      if (!row.value) {
        return (
          <div key={index} className="py-2 px-6">
            <span className="text-gray-500">No value available</span>
          </div>
        );
      }
      
      const value = row.value;
      const displayValue = row.invertBalance ? 
        formatCurrency((value * -1).toString()) : 
        formatCurrency(value.toString());
      
      return (
        <div key={index} className="flex justify-between items-center py-2 px-6 bg-blue-50 border-t border-blue-200">
          <span className="font-medium text-blue-800">{row.label}</span>
          <span className="font-bold text-blue-800">{displayValue}</span>
        </div>
      );
    }
    
    if (row.rowType === 'summary') {
      return null;
    }
    
    return (
      <div key={index} className="py-2 px-4 text-gray-500">
        Unknown row type: {row.rowType}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Row: Date + Report Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CustomInput
              type="date"
              name="reportEndDate"
              label="End Date"
              value={request.reportEndDate}
              onChange={(val) => setRequest({ ...request, reportEndDate: val })}
              required
            />

            <FormSelect
              label="Type"
              value={request.type}
              options={BALANCE_SHEET_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(val) => setRequest({ ...request, type: val as ReportType })}
              isSearchable={false}
            />

            <FormSelect
              label="Journal Status"
              value={request.journalStatus}
              options={[
                { value: 'all', label: 'All' },
                { value: 'draft', label: 'Draft' },
                { value: 'posted', label: 'Posted' },
              ]}
              onChange={(val) => setRequest({ ...request, journalStatus: val as any })}
              isSearchable={false}
            />
          </div>

          {/* Second Row: Comparative Options + Button */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormSelect
              label="Comparative Type"
              value={request.comparativeColumnType || ''}
              options={[
                { value: '', label: 'None' },
                { value: 'month', label: 'Month' },
                { value: 'quarter', label: 'Quarter' },
                { value: 'year', label: 'Year' },
              ]}
              onChange={(val) => setRequest({
                ...request,
                comparativeColumnType: (val as 'month' | 'quarter' | 'year') || undefined,
                comparativeColumnCount: undefined,
              })}
              isSearchable={false}
            />

            <CustomInput
              type="number"
              name="comparativeColumnCount"
              label="Comparative Count"
              value={String(request.comparativeColumnCount || '')}
              onChange={(val) => setRequest({ ...request, comparativeColumnCount: val ? parseInt(val) : undefined })}
              disabled={!request.comparativeColumnType}
              placeholder={request.comparativeColumnType ? `1-${request.comparativeColumnType === 'year' ? 2 : request.comparativeColumnType === 'quarter' ? 4 : 12}` : 'Select type first'}
            />

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center h-[calc(3.5rem+2px)]"
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Content */}
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {noData && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800">No balance sheet data available for the selected criteria.</p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading balance sheet...</span>
          </div>
        )}

        {!loading && balanceSheet && (
          <div className="space-y-4">
            <div className="space-y-2">
              {balanceSheet.rows.length > 0 ? (
                balanceSheet.rows.map((row, index) => renderRow(row, index))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No data available in the balance sheet response.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !balanceSheet && !error && !noData && (
          <div className="space-y-4">
            <div className="text-center py-12 text-gray-500">
              <div className="mb-4">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Generate Balance Sheet</h3>
              <p className="text-gray-600">Configure your report parameters above and click "Generate Report" to view your balance sheet.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BalanceSheet;
